import React from 'react'
import {
  GoogleLogin as Login,
  GoogleLoginResponse,
  GoogleLoginResponseOffline
} from 'react-google-login'
import GoogleLogo from './glogo.svg'
import styles from './styles.css'
import classNames from 'classnames'
import config from '#src/config'
import { GoogleSigninResponse } from '#src/services/dealNativeBridge'
import { isConsumerMobileNative } from '#src/services/mobile'
import {
  WebMessageEventEmitterConsumer,
  WebMessageEventEmitter
} from '#src/containers/ReceiveMessageFromNative'
import uuidv4 from 'uuid/v4'

export interface GoogleAuthenticateSuccess {
  user: {
    id: string | null
    name: string | null
    email: string | null
    photo: string | null
    familyName: string | null
    givenName: string | null
  }
  idToken: string
}

export interface GoogleLoginProps {
  className?: string
  text: string
  scope?: string
  onSuccess: (response: GoogleAuthenticateSuccess) => void
  onFailure?: (error: any) => void
}

export default class GoogleLogin extends React.Component<GoogleLoginProps> {
  constructor(props: GoogleLoginProps) {
    super(props)

    this.handleFailure = this.handleFailure.bind(this)
  }

  private handleFailure(error: any) {
    if (this.props.onFailure) {
      this.props.onFailure(error)
    }
  }

  public render() {
    return (
      <>
        <WrappedLoginWebOrNative
          className={this.props.className}
          onSuccess={this.props.onSuccess}
          onFailure={this.handleFailure}
        >
          <div className={styles.container}>
            <div className={styles.logo}>
              <GoogleLogo className={styles.icon} />
            </div>
            <div className={styles.text}>{this.props.text}</div>
          </div>
        </WrappedLoginWebOrNative>
      </>
    )
  }
}
interface WrappedLoginWebOrNativeProps {
  className?: string
  onSuccess: (response: GoogleAuthenticateSuccess) => void
  onFailure: (error: any) => void
  children: React.ReactNode
}

const WrappedLoginWebOrNative: React.SFC<WrappedLoginWebOrNativeProps> = props => (
  <WebMessageEventEmitterConsumer>
    {eventEmitter => <LoginWebOrNative {...props} eventEmitter={eventEmitter} />}
  </WebMessageEventEmitterConsumer>
)

interface LoginWebOrNativeProps extends WrappedLoginWebOrNativeProps {
  eventEmitter?: WebMessageEventEmitter
}

class LoginWebOrNative extends React.Component<LoginWebOrNativeProps> {
  constructor(props: LoginWebOrNativeProps) {
    super(props)

    this.handleSuccessWeb = this.handleSuccessWeb.bind(this)
    this.handleFailureWeb = this.handleFailureWeb.bind(this)
    this.handleNativeClick = this.handleNativeClick.bind(this)
  }
  private handleSuccessWeb(response: GoogleLoginResponse | GoogleLoginResponseOffline) {
    const loginResponse = response as GoogleLoginResponse
    const basicProfile = loginResponse.getBasicProfile()
    this.props.onSuccess({
      user: {
        id: basicProfile.getId(),
        name: basicProfile.getName(),
        email: basicProfile.getEmail(),
        photo: basicProfile.getImageUrl(),
        familyName: basicProfile.getFamilyName(),
        givenName: basicProfile.getGivenName()
      },
      idToken: loginResponse.getAuthResponse().id_token
    })
  }

  private handleFailureWeb(error: any) {
    this.props.onFailure(error)
  }

  private handleNativeClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    const { eventEmitter, onSuccess, onFailure } = this.props
    eventEmitter!
      .requestResponse<GoogleSigninResponse>(
        {
          type: 'googleSigninRequest',
          messageId: uuidv4()
        },
        'googleSigninResponse'
      )
      .then(message => {
        if (message.success) {
          onSuccess(message.success)
        } else {
          onFailure(message.failureCode)
        }
      })
  }

  render() {
    // Don't SSR since we can't determine the difference between being embedded in
    // the consumer mobile native app vs regular web at this point. If we allow this
    // to SSR, the server will render the <Login> component, and then the mobile native web
    // app (web view wrapper) will rehydrate DOM generated by <Login> with the virtual dom
    // in the regular <button> code path, leading to some visual awkwardness.
    if (process.env.TARGET === 'node') {
      return null
    }

    const { className, children, eventEmitter } = this.props
    if (isConsumerMobileNative() && eventEmitter) {
      return (
        <button className={classNames(styles.button, className)} onClick={this.handleNativeClick}>
          {children}
        </button>
      )
    } else {
      return (
        <Login
          className={classNames(styles.button, className)}
          clientId={config.get('google.client_id')}
          responseType="id_token"
          onSuccess={this.handleSuccessWeb}
          onFailure={this.handleFailureWeb}
        >
          {children}
        </Login>
      )
    }
  }
}
